import React, { useMemo, useEffect, useState } from 'react';
import { useControllableState } from 'hooks/use-controllable-state';
import DoughnutSegment from './doughnut-segment';

type DataSegment = {
  value: number;
  color: string;
};

type DoughnutChartProps<TDataSegment extends DataSegment> = {
  segments: TDataSegment[];
  selectedIndex?: number;
  hoverIndex?: number | null;
  onChangeSelectedIndex?: (index: number) => void;
  onChangeHoverIndex?: (index: number | null) => void;
};

const CHART_CONFIG = {
  width: 200,
  height: 200,
  centerX: 100,
  centerY: 100,
  radius: 70,
  strokeWidth: 12,
  gapOffset: 2,
  minAngle: 6
} as const;

/**
 * Calculates the angles for each segment in the doughnut chart
 */
const calculateSegments = (segments: DataSegment[]) => {
  if (segments.length === 1) {
    return [
      {
        ...segments[0],
        startAngle: 0,
        endAngle: 359.99 // Use slightly less than 360 to ensure the path is drawn
      }
    ];
  }

  const total = segments.reduce((sum, { value }) => sum + value, 0);
  const circumference = 2 * Math.PI * CHART_CONFIG.radius;
  const gapAngle = (CHART_CONFIG.gapOffset / circumference) * 360;
  const availableAngle = 360 - segments.length * gapAngle;

  // Calculate initial angles based on values
  const initialAngles = segments.map(({ value }) => (value / total) * 360);

  // Ensure minimum angle for visibility
  const adjustedAngles = initialAngles.map(angle => (angle < CHART_CONFIG.minAngle ? CHART_CONFIG.minAngle : angle));

  // If total exceeds available angle, scale down non-minimum segments
  const totalAdjusted = adjustedAngles.reduce((sum, angle) => sum + angle, 0);
  const surplus = totalAdjusted - availableAngle;

  if (surplus > 0) {
    const variableIndices = initialAngles
      .map((angle, i) => (angle >= CHART_CONFIG.minAngle ? i : -1))
      .filter(i => i !== -1);

    const variableTotal = variableIndices.reduce((sum, i) => sum + initialAngles[i], 0);

    if (variableTotal > 0) {
      variableIndices.forEach(i => {
        const reduction = (initialAngles[i] / variableTotal) * surplus;
        adjustedAngles[i] = Math.max(initialAngles[i] - reduction, CHART_CONFIG.minAngle);
      });
    }
  }

  // Calculate start and end angles for each segment
  let currentAngle = 0;
  return adjustedAngles.map((angle, i) => {
    const startAngle = currentAngle;
    const endAngle = startAngle + angle;
    currentAngle = endAngle + gapAngle;
    return {
      ...segments[i],
      startAngle,
      endAngle
    };
  });
};

const DoughnutChart = <TDataSegment extends DataSegment>({
  segments,
  selectedIndex: controlledSelectedIndex,
  hoverIndex: controlledHoverIndex,
  onChangeSelectedIndex,
  onChangeHoverIndex
}: DoughnutChartProps<TDataSegment>) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const [selectedIndex, setSelectedIndex] = useControllableState({
    prop: controlledSelectedIndex,
    defaultProp: 0,
    onChange: onChangeSelectedIndex
  });

  const [hoverIndex, setHoverIndex] = useControllableState({
    prop: controlledHoverIndex,
    defaultProp: null,
    onChange: onChangeHoverIndex
  });

  const { segmentsData, activeIndex } = useMemo(
    () => ({
      segmentsData: calculateSegments(segments),
      activeIndex: hoverIndex ?? selectedIndex
    }),
    [segments, hoverIndex, selectedIndex]
  );

  return (
    <svg
      width='100%'
      height='100%'
      viewBox='0 0 200 200'
      style={{
        opacity: isMounted ? 1 : 0,
        transform: isMounted ? 'scale(1)' : 'scale(0.8)',
        transition: 'opacity 0.5s ease, transform 0.5s ease'
      }}
    >
      {segmentsData.map((segment, index) => (
        <DoughnutSegment
          key={index}
          cx={CHART_CONFIG.centerX}
          cy={CHART_CONFIG.centerY}
          radius={CHART_CONFIG.radius}
          strokeWidth={CHART_CONFIG.strokeWidth}
          startAngle={segment.startAngle}
          endAngle={segment.endAngle}
          gapAngle={0}
          segment={segment}
          isActive={index === activeIndex}
          onMouseEnter={() => setHoverIndex(index)}
          onMouseLeave={() => setHoverIndex(null)}
          onClick={() => setSelectedIndex(index)}
          animateFill={isMounted}
        />
      ))}
    </svg>
  );
};

export default DoughnutChart;
